import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Heart,
  List,
  Play,
  RotateCcw,
  SkipBack,
  SkipForward,
  Undo2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import type { Episode, MediaSeries, PlaybackContinue } from "../api/types";
import { CommandButton } from "../components/CommandButton";
import { PageShell } from "../components/PageShell";
import {
  DEFAULT_ERROR_RECOVERY_ACTION,
  StatusBlock,
} from "../components/StatusBlock";
import { useAudioCoordinator } from "../stores/audioCoordinator";
import { useNavigationStore } from "../stores/navigationStore";
import styles from "../styles/App.module.css";
import {
  buildEpisodeRanges,
  displayEpisodeTitle,
  historyForPage,
} from "./seriesPagination";
type EpisodePage = {
  items: Episode[];
  next_cursor?: string | null;
  total: number;
};
export function SeriesPage({ seriesId }: { seriesId: string }) {
  const back = useNavigationStore((state) => state.back);
  const go = useNavigationStore((state) => state.go);
  const play = useAudioCoordinator((state) => state.play);
  const announce = useAudioCoordinator((state) => state.announce);
  const [series, setSeries] = useState<MediaSeries | null>(null);
  const [playbackContext, setPlaybackContext] =
    useState<PlaybackContinue | null>(null);
  const [episodePage, setEpisodePage] = useState<EpisodePage>({
    items: [],
    total: 0,
  });
  const [episodesLoaded, setEpisodesLoaded] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [history, setHistory] = useState<(string | null)[]>([]);
  const [showRanges, setShowRanges] = useState(false);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [completionPending, setCompletionPending] = useState(false);
  const completionPendingRef = useRef(false);
  useEffect(() => {
    api
      .mediaSeriesDetail(seriesId)
      .then(setSeries)
      .catch((loadError) =>
        setError(
          loadError instanceof Error ? loadError.message : "读取节目失败。",
        ),
      );
  }, [seriesId]);

  useEffect(() => {
    api
      .playbackContinue()
      .then(setPlaybackContext)
      .catch((loadError) =>
        setError(
          loadError instanceof Error ? loadError.message : "读取播放状态失败。",
        ),
      );
  }, [seriesId]);

  const refreshPlaybackAndEpisodes = useCallback(async () => {
    const [nextPlaybackContext, nextEpisodePage] = await Promise.all([
      api.playbackContinue(),
      api.episodes(seriesId, cursor, { refresh: true }),
    ]);
    setPlaybackContext(nextPlaybackContext);
    setEpisodePage({
      items: nextEpisodePage.items,
      next_cursor: nextEpisodePage.next_cursor,
      total: nextEpisodePage.total,
    });
  }, [cursor, seriesId]);

  useEffect(() => {
    let cancelled = false;
    const hadCachedEpisodes = api.hasCachedEpisodes(seriesId, cursor);
    setEpisodesLoaded(false);
    api
      .episodes(seriesId, cursor)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setEpisodePage({
          items: result.items,
          next_cursor: result.next_cursor,
          total: result.total,
        });
        if (hadCachedEpisodes) {
          void refreshPlaybackAndEpisodes().catch(() => undefined);
        }
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        setError(
          loadError instanceof Error ? loadError.message : "读取分集失败。",
        );
      })
      .finally(() => {
        if (!cancelled) {
          setEpisodesLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cursor, refreshPlaybackAndEpisodes, seriesId]);

  const firstEpisode = episodePage.items[0];
  const secondEpisode = episodePage.items[1];
  const playbackInSeries =
    playbackContext?.content_type === "media_episode" &&
    playbackContext.series_id === seriesId
      ? playbackContext
      : null;
  const previousEpisode = playbackInSeries?.previous_episode ?? null;
  const nextEpisode = playbackInSeries?.next_episode ?? secondEpisode ?? null;
  const episodeRanges = useMemo(() => {
    return buildEpisodeRanges(episodePage.total);
  }, [episodePage.total]);
  const rangeText = useMemo(() => {
    if (!episodesLoaded) {
      return "正在读取分集";
    }
    if (!episodePage.items.length) {
      return "暂无分集";
    }
    return `第 ${episodePage.items[0].episode_number} 到第 ${
      episodePage.items.at(-1)?.episode_number ?? 0
    } 集，共 ${episodePage.total} 集`;
  }, [episodePage, episodesLoaded]);

  function playEpisode(episode: Episode) {
    const title = displayEpisodeTitle(episode, series?.title);
    void play({
      title,
      episodeId: episode.id,
      seriesId: episode.series_id,
      contentType: "media_episode",
      grant: () => api.mediaGrant(episode.id),
    });
  }

  function playCurrentPosition(position: PlaybackContinue) {
    void play({
      title: position.title,
      episodeId: position.episode_id,
      seriesId: position.series_id,
      contentType: position.content_type,
      startAtSeconds: position.position_seconds,
      grant: () => api.mediaGrant(position.episode_id),
    });
  }

  async function updateCurrentEpisodeCompletion(completed: boolean) {
    if (completionPendingRef.current) {
      setActionMessage("正在更新听完状态，请稍等。");
      announce("正在更新听完状态，请稍等。");
      return;
    }

    if (!playbackInSeries) {
      announce("还没有当前播放记录，请先播放一集。");
      return;
    }

    completionPendingRef.current = true;
    setCompletionPending(true);
    setActionMessage("正在更新听完状态，请稍等。");
    setError("");
    try {
      const response = await (completed
        ? api.completeProgress({
            episode_id: playbackInSeries.episode_id,
            player_session_id: playbackInSeries.player_session_id,
            client_sequence: playbackInSeries.last_client_sequence + 1,
          })
        : api.uncompleteProgress({
            episode_id: playbackInSeries.episode_id,
            player_session_id: playbackInSeries.player_session_id,
            client_sequence: playbackInSeries.last_client_sequence + 1,
            position_seconds: playbackInSeries.position_seconds,
          }));
      if (!response.accepted) {
        setError("播放记录已经更新，请返回上一层后再进入。");
        setActionMessage("");
        return;
      }

      await refreshPlaybackAndEpisodes();
      setError("");
      setActionMessage("");
      announce(
        completed
          ? `${playbackInSeries.title} 已标记听完。`
          : `${playbackInSeries.title} 已撤销听完标记。`,
      );
    } catch (completionError) {
      setError(
        completionError instanceof Error
          ? completionError.message
          : "更新听完状态失败，请返回上一层后重试。",
      );
      setActionMessage("");
    } finally {
      completionPendingRef.current = false;
      setCompletionPending(false);
    }
  }

  function chooseRange(page: number, rangeCursor: string | null) {
    setCursor(rangeCursor);
    setHistory(historyForPage(page));
    setShowRanges(false);
  }

  return (
    <PageShell
      title={series?.title ?? "节目"}
      where={`${series?.title ?? "节目"}分集页`}
    >
      {error ? (
        <StatusBlock
          tone="error"
          message={error}
          recoveryAction={DEFAULT_ERROR_RECOVERY_ACTION}
        />
      ) : null}
      {actionMessage ? <StatusBlock message={actionMessage} /> : null}
      <p className={styles.rangeLabel}>{rangeText}</p>
      <div className={styles.commandList}>
        <CommandButton
          icon={RotateCcw}
          title="继续当前集"
          hint={
            playbackInSeries
              ? `${playbackInSeries.title}，从 ${Math.floor(
                  playbackInSeries.position_seconds,
                )} 秒继续`
              : firstEpisode
                ? displayEpisodeTitle(firstEpisode, series?.title)
                : episodesLoaded
                  ? "暂无可播放分集"
                  : "正在读取分集"
          }
          onClick={() => {
            if (playbackInSeries) {
              playCurrentPosition(playbackInSeries);
              return;
            }
            if (firstEpisode) {
              playEpisode(firstEpisode);
              return;
            }
            announce(
              episodesLoaded
                ? "暂时没有可播放分集。"
                : "正在读取分集，请稍等。",
            );
          }}
        />
        <CommandButton
          icon={SkipForward}
          title="下一集"
          hint={
            nextEpisode
              ? displayEpisodeTitle(nextEpisode, series?.title)
              : "已经是最后一集"
          }
          onClick={() => nextEpisode && playEpisode(nextEpisode)}
        />
        <CommandButton
          icon={SkipBack}
          title="上一集"
          hint={
            previousEpisode
              ? displayEpisodeTitle(previousEpisode, series?.title)
              : "已经是第一集"
          }
          onClick={() => previousEpisode && playEpisode(previousEpisode)}
        />
        <CommandButton
          icon={List}
          title="最近听过"
          hint="显示最近播放记录"
          onClick={() => go({ name: "recent" })}
        />
        <CommandButton
          icon={List}
          title="选择集数范围"
          hint={showRanges ? "收起集数范围" : "按 20 集一组选择"}
          onClick={() => setShowRanges((value) => !value)}
        />
        <CommandButton
          icon={Heart}
          title="我收藏的本系列"
          hint="收藏入口在分集操作顶部"
          onClick={() => go({ name: "favorites" })}
        />
        <CommandButton
          icon={ArrowLeft}
          title="返回听戏和评书菜单"
          onClick={back}
        />
        {playbackInSeries ? (
          <CommandButton
            icon={playbackInSeries.completed ? Undo2 : CheckCircle2}
            title={
              playbackInSeries.completed
                ? "撤销当前集听完标记"
                : "标记当前集已听完"
            }
            hint={
              completionPending
                ? "正在更新，请稍等"
                : playbackInSeries.completed
                  ? `${playbackInSeries.title}，恢复为未播放`
                  : `${playbackInSeries.title}，双击标记已听完`
            }
            onClick={() =>
              void updateCurrentEpisodeCompletion(!playbackInSeries.completed)
            }
          />
        ) : null}
        {!episodesLoaded ? (
          <StatusBlock message="正在读取分集。" />
        ) : episodePage.items.length ? null : (
          <StatusBlock message="暂时没有分集。请家人稍后添加节目。" />
        )}
        {showRanges
          ? episodeRanges.map((range) => (
              <CommandButton
                key={range.title}
                icon={List}
                title={range.title}
                hint={range.hint}
                onClick={() => chooseRange(range.page, range.cursor)}
              />
            ))
          : null}
        {episodesLoaded
          ? episodePage.items.map((episode, index) => (
              <CommandButton
                key={episode.id}
                icon={Play}
                title={displayEpisodeTitle(episode, series?.title)}
                hint={`第 ${index + 1} 项，共 ${
                  episodePage.items.length
                } 项，第 ${episode.episode_number} 集，${
                  episode.completed ? "已听完" : "未播放"
                }，双击播放`}
                onClick={() => playEpisode(episode)}
              />
            ))
          : null}
        {episodesLoaded && history.length ? (
          <CommandButton
            icon={ArrowLeft}
            title="上一组 20 集"
            onClick={() => {
              const nextHistory = history.slice(0, -1);
              setHistory(nextHistory);
              setCursor(history.at(-1) ?? null);
            }}
          />
        ) : null}
        {episodesLoaded && episodePage.next_cursor ? (
          <CommandButton
            icon={ArrowRight}
            title="下一组 20 集"
            onClick={() => {
              setHistory((value) => [...value, cursor]);
              setCursor(episodePage.next_cursor ?? null);
            }}
          />
        ) : null}
      </div>
    </PageShell>
  );
}
